import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Home,
  Building2,
  Users,
  ShieldCheck,
  Briefcase,
  Mail,
  MapPin,
  Layers,
  Wrench,
  ArrowRight,
} from 'lucide-react';
import Navbar from '../components/sections/Navbar';
import Footer from '../components/sections/Footer';

export default function SiteIndexPage() {
  const navigate = useNavigate();

  const goSection = (id) => {
    navigate('/');
    setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }, 140);
  };

  const GROUPS = [
    {
      title: 'Main Pages',
      testid: 'pages',
      links: [
        { icon: Home, label: 'Home', desc: 'The OMC homepage', action: () => { navigate('/'); window.scrollTo({ top: 0, behavior: 'smooth' }); } },
        { icon: Building2, label: 'About OMC', desc: 'Who we are and how we build', action: () => navigate('/about') },
        { icon: Users, label: 'Our Team', desc: 'Leadership and crews', action: () => navigate('/team') },
        { icon: ShieldCheck, label: 'Safety', desc: 'Our safety standard and record', action: () => navigate('/safety') },
        { icon: Briefcase, label: 'Careers', desc: 'Open roles and how to apply', action: () => navigate('/careers') },
        { icon: Mail, label: 'Contact', desc: 'Start a conversation with OMC', action: () => navigate('/contact') },
      ],
    },
    {
      title: 'On the Homepage',
      testid: 'homepage-sections',
      links: [
        { icon: MapPin, label: 'Project Map', desc: 'Interactive coverage map of completed projects nationwide', action: () => goSection('work') },
        { icon: Layers, label: 'Where We Work', desc: 'Water, Industrial, and Energy markets', action: () => goSection('markets') },
        { icon: Wrench, label: 'Capabilities', desc: 'Specialty civil and process-mechanical disciplines', action: () => goSection('capabilities') },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="site-index-page">
      <Navbar />
      <main id="main-content" tabIndex={-1}>
        {/* Hero band */}
        <section className="relative bg-coal text-bone overflow-hidden pt-20 sm:pt-24 pb-16 sm:pb-20 border-b-4 border-[hsl(var(--forest))]">
          <div className="absolute inset-0 bg-grain opacity-[0.05] pointer-events-none" />
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              backgroundImage:
                'radial-gradient(ellipse 60% 50% at 20% 20%, hsl(80 57% 31% / 0.14) 0%, transparent 70%)',
            }}
          />
          <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <p className="kicker kicker-on-dark mb-4" data-testid="site-index-kicker">Site Index</p>
            <h1 className="editorial-headline text-4xl sm:text-5xl lg:text-6xl text-bone leading-[1.05] mb-6" data-testid="site-index-headline">
              Find Your Way
              <span className="italic font-bold text-forest-2"> Around.</span>
            </h1>
            <p className="text-bone/75 leading-[1.75] text-[15px] sm:text-lg max-w-2xl">
              A complete directory of everything on this website — every page, plus the
              key sections of the homepage. Looking for our nationwide coverage map? It&rsquo;s
              listed under <span className="text-bone">Project Map</span>.
            </p>
          </div>
        </section>

        {/* Index groups */}
        <section className="relative py-16 sm:py-20 bg-background overflow-hidden">
          <div className="absolute inset-0 bg-grain opacity-[0.025] pointer-events-none" />
          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14">
              {GROUPS.map((group) => (
                <div key={group.title} data-testid={`site-index-group-${group.testid}`}>
                  <p className="kicker mb-5">{group.title}</p>
                  <ul className="space-y-3">
                    {group.links.map((link, i) => {
                      const Icon = link.icon;
                      const slug = link.label.toLowerCase().replace(/\s+/g, '-');
                      return (
                        <motion.li
                          key={link.label}
                          initial={{ opacity: 0, y: 12 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.4, delay: i * 0.05 }}
                        >
                          <button
                            onClick={link.action}
                            data-testid={`site-index-link-${slug}`}
                            className="group w-full text-left rounded-xl border border-border bg-background hover:border-[hsl(var(--forest)/0.5)] hover:shadow-elev-2 transition-[border-color,box-shadow,transform] duration-300 hover:-translate-y-0.5 p-4 sm:p-5 flex items-center gap-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--forest))] focus-visible:ring-offset-2"
                          >
                            <span className="w-11 h-11 rounded-lg bg-[hsl(var(--forest)/0.10)] border border-[hsl(var(--forest)/0.28)] flex items-center justify-center shrink-0">
                              <Icon className="w-5 h-5 text-[hsl(var(--forest))]" strokeWidth={1.7} />
                            </span>
                            <span className="min-w-0 flex-1">
                              <span className="block font-display font-semibold text-lg text-foreground leading-tight">
                                {link.label}
                              </span>
                              <span className="block text-[13px] text-foreground/65 leading-snug mt-0.5">
                                {link.desc}
                              </span>
                            </span>
                            <ArrowRight className="w-5 h-5 text-foreground/40 group-hover:text-[hsl(var(--forest))] group-hover:translate-x-1 transition-[color,transform] duration-300 shrink-0" />
                          </button>
                        </motion.li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
